export type ClientRef = { id: string; company_name: string };
export type VenueRef = { id: string; name: string; city?: string | null };
export type RoomRef = { id: string; name: string; capacity?: number | null };

export type DeskClient = {
  id: string;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  cimso_client_id: number | null;
  created_at: string;
  updated_at: string;
};

export type DeskEnquiry = {
  id: string;
  client_id: string | null;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  event_name: string | null;
  preferred_start: string | null;
  preferred_end: string | null;
  guest_count: number | null;
  status: string;
  source: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  domeec_clients?: ClientRef | null;
};

export type DeskEventRoom = {
  id: string;
  event_id: string;
  room_id: string;
  starts_at: string | null;
  ends_at: string | null;
  setup: string | null;
  layout: string | null;
  pax: number | null;
  notes: string | null;
  domeec_rooms?: RoomRef | null;
};

export type DeskEvent = {
  id: string;
  enquiry_id: string | null;
  client_id: string | null;
  name: string;
  booking_reference?: string | null;
  status: string;
  starts_at: string | null;
  ends_at: string | null;
  venue_label: string | null;
  venue_id: string | null;
  room_id: string | null;
  guest_count: number | null;
  notes: string | null;
  source?: 'local' | 'cimso' | 'import';
  cimso_booking_id?: number | null;
  cimso_function_id?: number | null;
  cimso_event_id?: number | null;
  cimso_synced_at?: string | null;
  local_override_fields?: string[];
  created_at: string;
  updated_at: string;
  domeec_clients?: ClientRef | null;
  domeec_venues?: VenueRef | null;
  domeec_rooms?: RoomRef | null;
  domeec_event_rooms?: DeskEventRoom[];
};

export type DeskFunctionSheet = {
  id: string;
  event_id: string | null;
  cimso_booking_id: number | null;
  cimso_function_id: number | null;
  cimso_document_id: number | null;
  title: string | null;
  status: string | null;
  starts_at: string | null;
  ends_at: string | null;
  room_name: string | null;
  venue_name: string | null;
  setup: string | null;
  layout: string | null;
  pax: number | null;
  menus: unknown[];
  catering: unknown[];
  av: unknown[];
  notes: string | null;
  line_items: Array<{
    description: string;
    category: string;
    quantity: number | null;
    unitPrice?: number | null;
    amount: number | null;
    notes: string | null;
  }>;
  charges: Array<{
    description: string;
    category: string;
    quantity: number | null;
    amount: number | null;
  }>;
  raw: Record<string, unknown>;
  cimso_synced_at: string | null;
  created_at: string;
  updated_at: string;
};

export type CimsoSyncResult = {
  status: 'ok' | 'partial' | 'failed' | 'dry_run' | 'disabled';
  dryRun: boolean;
  runId: string | null;
  window: {
    openingDay: number;
    closingDay: number;
    fromIso: string;
    toIso: string;
  };
  stats: {
    bookingsSeen: number;
    bookingIdsFetched?: number;
    skippedNoStartsAt?: number;
    skippedNotBooking?: number;
    skippedOutsideWindow?: number;
    skippedPlaceholder?: number;
    stoppedPastWindow?: boolean;
    eventsWouldCreate: number;
    eventsWouldUpdate: number;
    eventsUpserted: number;
    clientsWouldCreate: number;
    clientsWouldUpdate: number;
    clientsUpserted: number;
    sheetsWouldUpsert: number;
    sheetsUpserted: number;
    messagesCalled: string[];
    bookingListVariantUsed?: string | null;
    timedOut?: boolean;
    mode?: 'incremental' | 'backfill' | 'full';
    cursor?: {
      highWaterBookingId: number | null;
      backfillBookingId: number | null;
      mode: 'incremental' | 'backfill' | 'full';
    };
    errors: string[];
    warnings?: string[];
  };
  sample: Array<{
    cimsoBookingId: number;
    name: string;
    startsAt: string | null;
    venueLabel: string | null;
    guestCount: number | null;
    lineItemCount: number;
    action: 'create' | 'update' | 'unchanged';
  }>;
  debugSamples?: Array<{
    message: string;
    errorCode?: number | null;
    warning?: string;
    fieldNames: string[];
    topLevelKeys: string[];
    rowCount: number;
    rows: Record<string, unknown>[];
  }>;
  error?: string;
};

export type DeskVenue = {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  active: boolean;
  domeec_rooms?: DeskRoom[];
};

export type DeskRoom = {
  id: string;
  venue_id: string;
  name: string;
  capacity: number | null;
  layouts: unknown;
  rate_notes: string | null;
  active: boolean;
  domeec_venues?: VenueRef | null;
};

export type QuoteLine = {
  id: string;
  quote_id: string;
  description: string;
  category: string;
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  sort_order: number;
};

export type DeskQuote = {
  id: string;
  event_id: string | null;
  client_id: string | null;
  quote_number: string;
  status: string;
  currency: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  valid_until: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  domeec_clients?: ClientRef | null;
  domeec_events?: { id: string; name: string } | null;
  domeec_quote_line_items?: QuoteLine[];
};

export type InvoiceLine = {
  id: string;
  invoice_id: string;
  description: string;
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  sort_order: number;
};

export type DeskInvoice = {
  id: string;
  quote_id: string | null;
  event_id: string | null;
  client_id: string | null;
  invoice_number: string;
  status: string;
  currency: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  due_at: string | null;
  created_at: string;
  updated_at: string;
  domeec_clients?: ClientRef | null;
  domeec_events?: { id: string; name: string } | null;
  domeec_invoice_line_items?: InvoiceLine[];
};

export type DeskRateCard = {
  id: string;
  code: string;
  name: string;
  category: string;
  unit: string;
  unit_price_cents: number;
  currency: string;
  is_active: boolean;
};

export type DeskAttachment = {
  id: string;
  entity_type: string;
  entity_id: string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  storage_path: string;
  created_at: string;
};

export type DeskSettings = {
  id: string;
  company_name: string;
  legal_name: string | null;
  registration_no: string | null;
  vat_no: string | null;
  vat_rate: number;
  address: string | null;
  phone: string | null;
  email_from: string | null;
  logo_storage_path: string | null;
  brand_colours: Record<string, string> | null;
  banking_details: Record<string, unknown> | null;
  quote_terms: string | null;
  invoice_terms: string | null;
  quote_number_prefix: string;
  invoice_number_prefix: string;
  next_quote_sequence: number;
  next_invoice_sequence: number;
  created_at: string;
  updated_at: string;
};

export type DashboardSummary = {
  counts: {
    openEnquiries: number;
    activeEvents: number;
    openQuotes: number;
    openInvoices: number;
    clients: number;
  };
  recentEnquiries: Array<{
    id: string;
    contact_name: string;
    event_name: string | null;
    status: string;
    created_at: string;
  }>;
  upcomingEvents: Array<{
    id: string;
    name: string;
    status: string;
    starts_at: string | null;
    venue_label: string | null;
    venue_id?: string | null;
    room_id?: string | null;
    domeec_venues?: { id: string; name: string } | null;
    domeec_rooms?: { id: string; name: string } | null;
  }>;
};

/** Human-readable venue · room for list/detail cells. */
export function formatEventPlace(ev: {
  venue_label?: string | null;
  domeec_venues?: { name?: string | null } | null;
  domeec_rooms?: { name?: string | null } | null;
}): string {
  const venue = ev.domeec_venues?.name || ev.venue_label || null;
  const room = ev.domeec_rooms?.name || null;
  if (venue && room) return `${venue} · ${room}`;
  return venue || room || '—';
}
